import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { supabase } from '../config/supabase';
import {
  SUBSCRIPTION_PLANS,
  getCreditUsageSummary,
  getImageCredits,
  getVideoCredits
} from '../config/subscriptionPlans';
import { useAuth } from './AuthContext';
import { getUserPlanFromDatabase } from '../utils/planUtils';

export interface CreditBalance {
  displayCreditsRemaining: number;
  displayCreditsTotal: number;
  displayCreditsUsed: number;
}

interface CreditContextValue {
  creditBalance: CreditBalance | null;
  creditsLoading: boolean;
  refreshCredits: () => Promise<void>;
}

const CreditContext = createContext<CreditContextValue | undefined>(undefined);

export const CreditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(false);

  const fetchCreditBalance = useCallback(async () => {
    if (!user?.id) {
      setCreditBalance(null);
      setCreditsLoading(false);
      return;
    }

    setCreditsLoading(true);
    try {
      // CRITICAL: Always fetch the latest subscription_plan from database
      // This ensures we get the updated plan after a payment webhook
      // Don't rely solely on AuthContext user object which may be stale
      let subscriptionPlanFromDb = user.subscription_plan;
      try {
        // Check if we're using JWT token authentication (admin impersonation)
        const isUsingJwtToken = typeof window !== 'undefined' && !!localStorage.getItem('auth_token');
        
        if (isUsingJwtToken) {
          // When using JWT token, query backend API instead of Supabase
          // (Supabase may not have the JWT token and RLS policies may block the query)
          const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const response = await fetch(`${backendUrl}/api/v1/auth/verify-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.user?.subscription_plan) {
              subscriptionPlanFromDb = data.user.subscription_plan;
              if (subscriptionPlanFromDb !== user.subscription_plan) {
                console.log('[CreditContext] Subscription plan mismatch detected:', {
                  authContextPlan: user.subscription_plan,
                  databasePlan: subscriptionPlanFromDb,
                  userId: user.id
                });
              }
            }
          }
        } else {
          // Normal Supabase query when using regular session
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('subscription_plan')
            .eq('id', user.id)
            .single();
          
          if (userProfile?.subscription_plan) {
            subscriptionPlanFromDb = userProfile.subscription_plan;
            // Log if there's a mismatch between AuthContext and database
            if (subscriptionPlanFromDb !== user.subscription_plan) {
              console.log('[CreditContext] Subscription plan mismatch detected:', {
                authContextPlan: user.subscription_plan,
                databasePlan: subscriptionPlanFromDb,
                userId: user.id
              });
            }
          }
        }
      } catch (dbError) {
        console.warn('Failed to fetch subscription_plan from database, using AuthContext value:', dbError);
        // Fall back to AuthContext value if database fetch fails
      }

      // Default to free plan (0 credits) - only offered plans are: explorer, a_la_carte, premium
      // Default to free plan (0 credits)
      // Currently offered plans: explorer (DIY 800), a_la_carte (3 videos), premium (monthly)
      let userPlan = SUBSCRIPTION_PLANS.free;

      if (subscriptionPlanFromDb) {
        const dbPlan = await getUserPlanFromDatabase(subscriptionPlanFromDb);
        if (dbPlan) {
          userPlan = dbPlan;
        } else if (subscriptionPlanFromDb !== 'free') {
          // If a specific plan was requested but not found in database, log it
          console.warn('[CreditContext] Could not find plan in database:', subscriptionPlanFromDb, '- using free plan as fallback');
        }
      }

      // Determine if this is a one-time plan BEFORE querying generations
      // For one-time plans (explorer, a_la_carte), calculate credits from ALL generations
      // For subscription plans, only count current month (credits reset monthly)
      const isOneTimePlan = subscriptionPlanFromDb === 'explorer' || subscriptionPlanFromDb === 'a_la_carte';
      
      const now = new Date();
      // Get first day of current month at 00:00:00 UTC
      const currentMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
      const nextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));

      // Query generations table from database as source of truth
      // Select only necessary fields for credit calculation
      // Note: duration_seconds and generation_type columns don't exist in schema
      // Using model_type to determine video vs image, and metadata for video duration
      // For one-time plans: fetch ALL completed generations (no date filter)
      // For subscription plans: filter by current month only (credits reset monthly)
      let generations: any[] | null = null;
      let error: any = null;
      
      // Check if we're using JWT token authentication (admin impersonation)
      const isUsingJwtToken = typeof window !== 'undefined' && !!localStorage.getItem('auth_token');
      
      if (isUsingJwtToken) {
        // When using JWT token, try user endpoint first (queries generations table directly)
        // Fall back to admin endpoint if needed
        try {
          const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const authToken = localStorage.getItem('auth_token');
          
          console.log('[CreditContext] Fetching generations via backend API for user:', user.id, 'isOneTimePlan:', isOneTimePlan);
          
          // Fetch all generations with pagination (max 100 per page)
          // Use user endpoint which queries generations table directly (no join with user_profiles)
          let allGenerations: any[] = [];
          let page = 1;
          const limit = 100;
          let hasMore = true;
          let useAdminEndpoint = false; // Try user endpoint first
          
          while (hasMore) {
            // Try user endpoint first (simpler, queries generations table directly)
            const endpoint = useAdminEndpoint 
              ? '/api/v1/admin/generations'
              : '/api/v1/user/generations';
            
            const params = new URLSearchParams({
              userId: user.id,
              page: page.toString(),
              limit: limit.toString(),
              status: 'completed' // Only fetch completed generations (failed ones don't count)
            });
            
            const response = await fetch(`${backendUrl}${endpoint}?${params}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (response.ok) {
              const data = await response.json();
              
              // Handle different response structures: user endpoint vs admin endpoint
              // User endpoint: { success: true, data: { generations: [...], totalCount: ... } }
              // Admin endpoint: { success: true, data: { generations: [...], total: ... } }
              const generationsArray = data.data?.generations || data.generations || [];
              const totalCount = data.data?.total || data.data?.totalCount || 0;
              
              console.log(`[CreditContext] Page ${page} API response (${useAdminEndpoint ? 'admin' : 'user'} endpoint):`, {
                success: data.success,
                total: totalCount,
                generationsCount: generationsArray.length,
                hasData: generationsArray.length > 0,
                endpoint
              });
              
              if (data.success && generationsArray.length > 0) {
                // Explicitly filter out deleted and failed generations (double-check)
                // This ensures we only count completed, non-deleted generations
                const pageGenerations = generationsArray.filter((g: any) => {
                  const isValid = !g.is_deleted && g.status === 'completed';
                  if (!isValid) {
                    console.log(`[CreditContext] Filtered out generation ${g.id}:`, {
                      is_deleted: g.is_deleted,
                      status: g.status
                    });
                  }
                  return isValid;
                });
                
                console.log(`[CreditContext] Page ${page}: Fetched ${pageGenerations.length} valid completed generations (total in response: ${generationsArray.length})`);
                
                // Log sample generation for debugging
                if (pageGenerations.length > 0) {
                  console.log(`[CreditContext] Sample generation:`, {
                    id: pageGenerations[0].id,
                    model_type: pageGenerations[0].model_type,
                    status: pageGenerations[0].status,
                    created_at: pageGenerations[0].created_at,
                    is_deleted: pageGenerations[0].is_deleted
                  });
                }
                
                // For one-time plans, include all generations
                // For subscription plans, filter by current month
                const filteredGenerations = isOneTimePlan
                  ? pageGenerations
                  : pageGenerations.filter((g: any) => {
                      const createdDate = new Date(g.created_at);
                      const inMonth = createdDate >= currentMonth && createdDate < nextMonth;
                      if (!inMonth) {
                        console.log(`[CreditContext] Generation ${g.id} filtered out by date:`, {
                          created_at: g.created_at,
                          currentMonth: currentMonth.toISOString(),
                          nextMonth: nextMonth.toISOString()
                        });
                      }
                      return inMonth;
                    });
                
                console.log(`[CreditContext] Page ${page}: After date filter: ${filteredGenerations.length} generations`);
                
                allGenerations = [...allGenerations, ...filteredGenerations];
                
                // Check if there are more pages
                // totalCount is the total of completed generations (since we filter by status='completed')
                // Continue if we got a full page of results (might be more pages)
                // OR if we haven't reached the total count yet
                hasMore = pageGenerations.length === limit && allGenerations.length < totalCount;
                console.log(`[CreditContext] Page ${page} pagination:`, {
                  pageGenerationsLength: pageGenerations.length,
                  limit,
                  allGenerationsLength: allGenerations.length,
                  totalCount,
                  hasMore
                });
                page++;
              } else {
                console.warn('[CreditContext] Backend API returned unsuccessful response or no generations:', {
                  success: data.success,
                  hasData: !!data.data,
                  hasGenerations: !!data.data?.generations,
                  dataKeys: data.data ? Object.keys(data.data) : []
                });
                hasMore = false;
              }
            } else {
              // If user endpoint fails, try admin endpoint as fallback (only once)
              if (!useAdminEndpoint && response.status === 403 || response.status === 404) {
                console.log('[CreditContext] User endpoint failed, trying admin endpoint as fallback...');
                useAdminEndpoint = true;
                page = 1; // Reset to first page
                allGenerations = []; // Reset generations
                continue; // Retry with admin endpoint
              }
              
              const errorText = await response.text().catch(() => 'Unknown error');
              console.error('[CreditContext] Backend API error:', {
                status: response.status,
                statusText: response.statusText,
                error: errorText,
                endpoint
              });
              hasMore = false;
              error = { message: `Backend API error: ${response.status}`, details: errorText };
            }
          }
          
          console.log(`[CreditContext] Total completed generations fetched: ${allGenerations.length}`);
          
          // If no generations found with status filter, try fetching all to debug
          if (allGenerations.length === 0 && !error) {
            console.warn('[CreditContext] No completed generations found, trying to fetch all generations for debugging...');
            try {
              const debugParams = new URLSearchParams({
                userId: user.id,
                page: '1',
                limit: '10'
                // No status filter - fetch all
              });
              
              const debugResponse = await fetch(`${backendUrl}/api/v1/admin/generations?${debugParams}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (debugResponse.ok) {
                const debugData = await debugResponse.json();
                console.log('[CreditContext] Debug - All generations (first 10):', {
                  total: debugData.data?.total,
                  generations: debugData.data?.generations?.map((g: any) => ({
                    id: g.id,
                    status: g.status,
                    is_deleted: g.is_deleted,
                    model_type: g.model_type,
                    created_at: g.created_at
                  }))
                });
              }
            } catch (debugErr) {
              console.error('[CreditContext] Debug fetch failed:', debugErr);
            }
          }
          
          generations = allGenerations;
        } catch (backendError) {
          console.error('[CreditContext] Backend API exception:', backendError);
          error = backendError;
          generations = [];
        }
      } else {
        // Normal flow: use Supabase
        console.log('[CreditContext] Fetching generations via Supabase for user:', user.id, 'isOneTimePlan:', isOneTimePlan);
        
        let query = supabase
          .from('generations')
          .select('id, model_type, status, created_at, is_deleted, metadata')
          .eq('user_id', user.id)
          .eq('status', 'completed') // Only fetch completed generations (failed ones don't count)
          .eq('is_deleted', false);
        
        // Only apply date filter for subscription plans (not one-time plans)
        if (!isOneTimePlan) {
          query = query
            .gte('created_at', currentMonth.toISOString())
            .lt('created_at', nextMonth.toISOString());
        }
        
        const result = await query;
        generations = result.data;
        error = result.error;
        
        if (error) {
          console.error('[CreditContext] Supabase query error:', error);
        } else {
          console.log(`[CreditContext] Supabase fetched ${(generations || []).length} completed generations`);
        }
      }

      if (error) {
        console.error('Error fetching credit balance from database:', error);
        // Don't return early - use empty array to show 0 credits used
        // This allows the UI to still display the plan's total credits
      }

      // Calculate credits used in DISPLAY credits from database generations
      // This matches the backend calculation logic exactly
      // The credit costs (40 per image, 240 per 6s video) are based on display credits
      // IMPORTANT: Only count completed generations (failed ones don't consume credits)
      let displayCreditsUsed = 0;
      let imageCount = 0;
      let videoCount = 0;
      
      // Double-check: filter out any failed or deleted generations that might have slipped through
      const validGenerations = (generations || []).filter((g: any) => {
        return g.status === 'completed' && !g.is_deleted;
      });
      
      console.log(`[CreditContext] Calculating credits from ${validGenerations.length} valid completed generations (total fetched: ${(generations || []).length})`);
      
      validGenerations.forEach((generation) => {
        // Check if it's a video generation - use model_type (generation_type column doesn't exist)
        const isVideoGeneration = generation.model_type?.startsWith('video_');
        
        if (isVideoGeneration) {
          // Get duration from metadata or default to 6 seconds (standard video length)
          const duration = generation.metadata?.duration || 
                          generation.metadata?.duration_seconds || 
                          6; // Default to 6 seconds for videos
          const videoCredits = getVideoCredits(duration);
          displayCreditsUsed += videoCredits;
          videoCount++;
        } else {
          // All other generations are images
          const imageCredits = getImageCredits(1);
          displayCreditsUsed += imageCredits;
          imageCount++;
        }
      });
      
      console.log(`[CreditContext] Credit calculation summary: ${imageCount} images, ${videoCount} videos, total credits: ${displayCreditsUsed}`);
      
      // Log detailed breakdown for debugging
      const videoBreakdown = (generations || [])
        .filter(g => g.model_type?.startsWith('video_'))
        .map(g => ({
          id: g.id,
          model_type: g.model_type,
          created_at: g.created_at,
          duration: g.metadata?.duration || g.metadata?.duration_seconds || 6,
          credits: getVideoCredits(g.metadata?.duration || g.metadata?.duration_seconds || 6)
        }));
      
      // Get prepaid credits from credit_transactions table (one-time purchases)
      let prepaidCredits = 0;
      try {
        // Try using RPC function first
        const { data: prepaidBalance, error: prepaidError } = await supabase.rpc('get_user_prepaid_credit_balance', {
          p_user_id: user.id
        });

        if (!prepaidError && prepaidBalance !== null && prepaidBalance !== undefined) {
          prepaidCredits = prepaidBalance;
        } else if (prepaidError) {
          // Fallback: calculate manually if RPC function fails
          console.warn('RPC function failed, calculating prepaid credits manually:', prepaidError);
          const now = new Date().toISOString();
          const { data: transactions } = await supabase
            .from('credit_transactions')
            .select('credits, expires_at, transaction_type')
            .eq('user_id', user.id)
            .eq('transaction_type', 'purchase')
            .or(`expires_at.is.null,expires_at.gt.${now}`);
          
          if (transactions) {
            prepaidCredits = transactions.reduce((sum, t) => {
              // Only count non-expired credits from purchase transactions
              if (!t.expires_at || new Date(t.expires_at) > new Date()) {
                return sum + (t.credits || 0);
              }
              return sum;
            }, 0);
          }
        }
      } catch (prepaidErr) {
        console.error('Error fetching prepaid credits:', prepaidErr);
        // Continue with 0 prepaid credits
      }

      // Get display credits total from plan (which comes from database plan_rules.monthly_generations_limit)
      // Database is the single source of truth for credit limits
      // For one-time purchase plans (explorer, a_la_carte), only use prepaid credits, ignore plan credits
      // IMPORTANT: Use subscriptionPlanFromDb (fetched from database) to ensure we have the latest plan
      // Note: isOneTimePlan was already determined above before querying generations
      const planDisplayCreditsTotal = isOneTimePlan ? 0 : (userPlan.features.displayCredits || userPlan.features.monthlyCredits);
      const displayCreditsTotal = planDisplayCreditsTotal + prepaidCredits; // Include prepaid credits
      const displayCreditsRemaining = Math.max(0, displayCreditsTotal - displayCreditsUsed);
      
      // Calculate actual credits for comparison (convert back from display)
      const actualCreditsTotal = userPlan.features.monthlyCredits;
      const actualCreditsUsed = actualCreditsTotal > 0 && planDisplayCreditsTotal > 0 
        ? Math.floor(displayCreditsUsed * (actualCreditsTotal / planDisplayCreditsTotal))
        : displayCreditsUsed;

      const summary = {
        displayCreditsTotal,
        displayCreditsUsed,
        displayCreditsRemaining,
        actualCreditsTotal,
        actualCreditsUsed,
        actualCreditsRemaining: Math.max(0, actualCreditsTotal - actualCreditsUsed),
        prepaidCredits // Include for debugging
      };

      console.log('[CreditContext] Credit balance calculated:', {
        userId: user.id,
        subscriptionPlan: subscriptionPlanFromDb,
        isOneTimePlan,
        planDisplayCreditsTotal,
        prepaidCredits,
        displayCreditsTotal,
        displayCreditsUsed,
        displayCreditsRemaining,
        generationsCount: (generations || []).length,
        imageCount,
        videoCount,
        usingJwtToken: isUsingJwtToken
      });

      setCreditBalance({
        displayCreditsRemaining: summary.displayCreditsRemaining,
        displayCreditsTotal: summary.displayCreditsTotal,
        displayCreditsUsed: summary.displayCreditsUsed
      });
    } catch (error) {
      console.error('Error calculating credit balance:', error);
    } finally {
      setCreditsLoading(false);
    }
  }, [user?.id, user?.subscription_plan]);

  const refreshCredits = useCallback(async () => {
    await fetchCreditBalance();
  }, [fetchCreditBalance]);

  useEffect(() => {
    fetchCreditBalance();
  }, [fetchCreditBalance]);
  
  // Refresh credits when subscription plan changes
  useEffect(() => {
    if (user?.subscription_plan) {
      // Small delay to ensure plan data is updated
      const timeoutId = setTimeout(() => {
        fetchCreditBalance();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [user?.subscription_plan, fetchCreditBalance]);

  const value = useMemo(
    () => ({
      creditBalance,
      creditsLoading,
      refreshCredits
    }),
    [creditBalance, creditsLoading, refreshCredits]
  );

  return <CreditContext.Provider value={value}>{children}</CreditContext.Provider>;
};

export const useCredits = () => {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
};


